import type { CatalogRow } from './r2Sync';
import {cloudReadFetch} from './cloudRead';

export type QueueStage = 'queued' | 'running';
export type QueueContext = { userId:string;token:string;signal:AbortSignal;onQueueStage?:(stage:QueueStage)=>void };
export type SyncJob = { id:string;documentId:string;documentNumber:string;status:QueueStage|'succeeded'|'failed'|'review'|'cancelled';error:string|null;result:{revision:number;deletedAt?:string|null;trashExpiresAt?:string|null}|null };
export type QueueOverview = { queued:number;running:number;review:number;maxParallel:number;notifications:SyncJob[] };

async function request(path:string,context:QueueContext,init:RequestInit={}) {
  let ack=false;if(typeof init.body==='string'){try{ack=JSON.parse(init.body)?.action==='read';}catch{/* A document write is never classified as an acknowledgement. */}}
  const response=await cloudReadFetch(`/api/sync/jobs${path}`,{...init,cache:'no-store',signal:context.signal,
    // Under the measured 100-account load, a single enqueue response took up
    // to 112 seconds. Keep one bounded write alive; never replay its POST.
    headers:{authorization:`Bearer ${context.token}`,'content-type':'application/json',...init.headers}},ack,init.method==='POST'&&!ack?120000:30000);
  if(!response.ok){const body=await response.json().catch(()=>({})) as {error?:string};throw new Error(body.error||`cloud_queue_http_${response.status}`);}
  return response;
}
export async function syncQueueEnabled(context:QueueContext){
  // A previous server version can be used during a rolling deployment. Auth,
  // network and server errors are never mistaken for a disabled queue.
  const response=await fetch('/api/sync/jobs?capabilities=1',{cache:'no-store',headers:{authorization:`Bearer ${context.token}`},signal:AbortSignal.any([context.signal,AbortSignal.timeout(30_000)])});
  if(response.status===404)return false;
  if(!response.ok)throw new Error(response.status===401?'session_expired':'cloud_queue_unavailable');
  const body=await response.json() as {enabled?:boolean;protocol?:string};
  if(typeof body.enabled!=='boolean')throw new Error('cloud_queue_unavailable');
  return body.enabled&&body.protocol==='queue-v1';
}
const pause=(signal:AbortSignal,ms:number)=>new Promise<void>((resolve,reject)=>{
  signal.throwIfAborted();
  const done=()=>{signal.removeEventListener('abort',abort);resolve();};
  const timer=setTimeout(done,ms);
  const abort=()=>{clearTimeout(timer);signal.removeEventListener('abort',abort);reject(signal.reason);};
  signal.addEventListener('abort',abort,{once:true});
});
export async function writeQueuedRow(row:CatalogRow,context:QueueContext){
  let job=await (await request('',context,{method:'POST',body:JSON.stringify(row)})).json() as SyncJob;
  const id=job.id;if(!id)throw new Error('invalid_cloud_job');
  const notify=()=>{if(typeof window!=='undefined'&&!context.signal.aborted)window.dispatchEvent(new CustomEvent('bilaflow:sync-queue-updated',{detail:context.userId}));};
  notify();
  let polls=0;
  for(;;){
    context.signal.throwIfAborted();
    if(job.id!==id||job.documentId!==row.id)throw new Error('invalid_cloud_job');
    if(job.status==='succeeded'){
      if(!job.result||!Number.isSafeInteger(job.result.revision)||job.result.revision<1)throw new Error('invalid_cloud_job');
      notify();
      return job.result;
    }
    if(job.status==='failed'||job.status==='cancelled'||job.status==='review'){notify();throw new Error(job.error||`cloud_queue_${job.status}`);}
    if(job.status!=='queued'&&job.status!=='running')throw new Error('invalid_cloud_job');
    context.onQueueStage?.(job.status);
    await pause(context.signal,polls++<3?1500:10000+(Number.parseInt(id.replaceAll('-','').slice(-4),16)||0)%2000);
    try { job=await (await request(`?id=${encodeURIComponent(id)}`,context)).json() as SyncJob; }
    catch(error){
      context.signal.throwIfAborted();
      // An accepted job remains durable if a status request loses connection.
      if(error instanceof Error&&/session_expired|not_found|401|403/.test(error.message))throw error;
      await pause(context.signal,4000);
    }
  }
}
export async function syncQueueOverview(context:QueueContext){return (await request('',context)).json() as Promise<QueueOverview>;}
export async function acknowledgeSyncJobs(ids:string[],context:QueueContext){await request('',context,{method:'POST',body:JSON.stringify({action:'read',ids})});}
